import React from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import DarkNav from '../components/dark-nav'
import Breadcrumbs from '../components/breadcrumbs'
import Footer from '../components/footer'
import { getCanonicalUrl } from '../utils/canonical'
import './sender-id-registration.css'

const SenderIdRegistration = () => {
  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'SMS Sender ID Registration' }
  ]

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'SMS Sender ID Registration — What You Need to Know',
    description: 'ACMA requires all SMS sender IDs used by Australian businesses to be registered from 1 July 2026. DataFlows submits on your behalf — here\'s what you need to do.',
    datePublished: '2025-11-01',
    dateModified: '2026-06-01',
    author: {
      '@type': 'Organization',
      name: 'DataFlows SMS',
      url: 'https://dataflows.com.au'
    },
    image: {
      '@type': 'ImageObject',
      url: 'https://dataflows.com.au/dataflows-high-resolution-logo-200h.png',
      width: 1200,
      height: 630
    },
    publisher: {
      '@type': 'Organization',
      name: 'DataFlows SMS',
      url: 'https://dataflows.com.au',
      logo: {
        '@type': 'ImageObject',
        url: 'https://dataflows.com.au/dataflows-high-resolution-logo-200h.png'
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://dataflows.com.au/sender-id-registration'
    }
  }

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What happens if my sender ID is not registered by 1 July 2026?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'From 1 July 2026, any SMS sent with an unregistered sender ID will be automatically labelled "Unverified" by Australian mobile carriers (Telstra, Optus, Vodafone). Your customers will see this warning label on your messages, which can reduce trust and open rates.'
        }
      },
      {
        '@type': 'Question',
        name: 'Does DataFlows handle the ACMA registration for me?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. DataFlows submits the registration application to ACMA on your behalf. You do not need to navigate the ACMA portal yourself. After we submit, ACMA will email your nominated contact directly to complete the confirmation step via myID.'
        }
      },
      {
        '@type': 'Question',
        name: 'What is myID and why do I need it?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'myID (myid.gov.au) is the Australian Government\'s digital identity app. It is used to securely verify your identity when accessing government portals including ACMA Assist. Your authorised representative must have a myID account set up before they can confirm the registration.'
        }
      },
      {
        '@type': 'Question',
        name: 'Who is the authorised representative?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'The authorised representative must be listed on the Australian Business Register (ABR) for your ABN — typically a director, public officer, or registered business contact. Their myID email address must match the contact email on the ABR.'
        }
      }
    ]
  }

  return (
    <div className="sid-container">
      <Helmet>
        <title>SMS Sender ID Registration Australia — What You Need to Know | DataFlows</title>
        <link rel="canonical" href={getCanonicalUrl('/sender-id-registration')} />
        <meta
          name="description"
          content="ACMA requires all Australian SMS sender IDs to be registered from 1 July 2026. DataFlows submits on your behalf — learn what information you need to provide and what to do when ACMA emails you."
        />
        <meta name="keywords" content="SMS Sender ID Registration Australia, ACMA Sender ID Register, SMS sender ID 2026, Australian SMS compliance, myID ACMA, Sender ID unverified Australia" />
        <meta property="og:title" content="SMS Sender ID Registration Australia — What You Need to Know | DataFlows" />
        <meta property="og:description" content="From 1 July 2026, unregistered sender IDs will be labelled 'Unverified' by Australian carriers. DataFlows registers on your behalf — here's how it works." />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <DarkNav />

      <div className="sid-breadcrumbs">
        <div className="sid-inner">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      {/* Deadline Banner */}
      <div className="sid-deadline-banner">
        <div className="sid-inner">
          <div className="sid-deadline-content">
            <span className="sid-deadline-icon">⚠️</span>
            <div>
              <strong>Action required before 1 July 2026</strong>
              <span>From this date, unregistered sender IDs will be labelled "Unverified" by Australian mobile carriers. Register now to protect your sender reputation.</span>
            </div>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="sid-hero">
        <div className="sid-inner">
          <div className="sid-hero-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            ACMA-Registered Originating Telco
          </div>
          <h1 className="sid-hero-title">SMS Sender ID Registration<br />— What You Need to Know</h1>
          <p className="sid-hero-subtitle">
            The Australian Government is introducing a mandatory SMS Sender ID Register as a new anti-scam initiative. DataFlows handles the ACMA submission for you — this page explains what happens next and what we need from you.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="sid-main">
        <div className="sid-inner sid-content-layout">

          {/* Section 1 — Why this is happening */}
          <section className="sid-section" id="why">
            <div className="sid-section-number">1</div>
            <div className="sid-section-body">
              <h2>Why this is happening</h2>
              <p>
                The Australian Government is introducing a mandatory SMS Sender ID Register as a new anti-scam initiative run by ACMA (the Australian Communications and Media Authority).
              </p>
              <p>
                From <strong>1 July 2026</strong>, any SMS message sent using an unregistered sender ID will be automatically labelled <strong>"Unverified"</strong> by Australian mobile carriers — Telstra, Optus, and Vodafone. This means your customers may see a warning label on your messages before they even open them.
              </p>
              <p>
                To avoid this, your sender ID must be registered before that date. The good news: DataFlows handles the technical submission to ACMA on your behalf.
              </p>
            </div>
          </section>

          <div className="sid-divider" />

          {/* Section 2 — What DataFlows does */}
          <section className="sid-section" id="what-dataflows-does">
            <div className="sid-section-number">2</div>
            <div className="sid-section-body">
              <h2>What DataFlows does for you</h2>
              <p>
                As your SMS provider and an ACMA-registered Originating Telco, DataFlows submits the Sender ID registration application to ACMA on your behalf.
              </p>
              <p>
                You don't need to navigate the ACMA portal yourself to start the process — we handle the technical submission. All you need to do is provide your business details through your DataFlows account (see Section 5 for the full list).
              </p>
              <div className="sid-highlight-box">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 16H12V12H11M12 8H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p>After we submit, ACMA will contact your nominated business representative directly by email to confirm the registration. You must complete this confirmation step — DataFlows cannot do it for you.</p>
              </div>
            </div>
          </section>

          <div className="sid-divider" />

          {/* Section 3 — What you need to do */}
          <section className="sid-section" id="confirmation-steps">
            <div className="sid-section-number">3</div>
            <div className="sid-section-body">
              <h2>What you need to do after DataFlows submits</h2>
              <p>Once we submit your application, ACMA will send a confirmation email to your nominated contact person. Here's exactly what to do when that email arrives:</p>

              <div className="sid-steps">
                <div className="sid-step">
                  <div className="sid-step-num">1</div>
                  <div className="sid-step-content">
                    <h3>Click the link in the ACMA email</h3>
                    <p>The email from ACMA contains a direct link to the correct page in the ACMA Assist portal. <strong>Use this link — do not try to navigate to ACMA Assist manually.</strong> The link takes you directly to your pre-filled registration.</p>
                  </div>
                </div>
                <div className="sid-step">
                  <div className="sid-step-num">2</div>
                  <div className="sid-step-content">
                    <h3>Log in using myID</h3>
                    <p>ACMA Assist uses <strong>myID</strong> — the Australian Government's digital identity app — to verify who you are. You'll need to have myID set up before you can log in. If you haven't set it up yet, see Section 7 below.</p>
                  </div>
                </div>
                <div className="sid-step">
                  <div className="sid-step-num">3</div>
                  <div className="sid-step-content">
                    <h3>The person logging in must be an authorised representative</h3>
                    <p>ACMA requires the person confirming the registration to be an authorised representative of the business as listed on the <strong>Australian Business Register (ABR)</strong> — such as a director or public officer. Their myID email must match the ABR contact email for your ABN.</p>
                    <p>If your ABR details are out of date, ACMA cannot confirm the registration (see Section 4).</p>
                  </div>
                </div>
                <div className="sid-step">
                  <div className="sid-step-num">4</div>
                  <div className="sid-step-content">
                    <h3>Confirm the registration in ACMA Assist</h3>
                    <p>Your sender ID details will be pre-filled. You simply select the applicable use case (e.g. Registered Business Name, Registered Domain Name) and click <strong>Save</strong>. That's it — your sender ID is registered.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <div className="sid-divider" />

          {/* Section 4 — ABR details */}
          <section className="sid-section" id="abr">
            <div className="sid-section-number">4</div>
            <div className="sid-section-body">
              <h2>Before we submit — check your ABR details are up to date</h2>
              <p>
                ACMA is seeing delays in registrations because many businesses have outdated information on the Australian Business Register. If the authorised representative's details on the ABR don't match, ACMA cannot confirm the registration.
              </p>
              <p>
                <strong>Before providing your details to DataFlows, check that your ABR listing is current at <a href="https://abr.business.gov.au" target="_blank" rel="noopener noreferrer">abr.business.gov.au</a>.</strong>
              </p>
              <div className="sid-checklist">
                <div className="sid-checklist-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#5228f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  <span>The contact email on the ABR must match the myID account of the person who will confirm the registration</span>
                </div>
                <div className="sid-checklist-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#5228f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  <span>The authorised representative must be listed on the ABR for your ABN (director, public officer, or registered contact)</span>
                </div>
                <div className="sid-checklist-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#5228f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  <span>Updating ABR details can take several business days — don't leave it until the last minute</span>
                </div>
              </div>
            </div>
          </section>

          <div className="sid-divider" />

          {/* Section 5 — What info to provide */}
          <section className="sid-section" id="information-required">
            <div className="sid-section-number">5</div>
            <div className="sid-section-body">
              <h2>What information you need to provide to DataFlows</h2>
              <p>To submit your registration, we'll need the following details from you. You can provide these through your DataFlows account or by contacting our support team.</p>
              <div className="sid-info-grid">
                <div className="sid-info-card">
                  <div className="sid-info-label">Business details</div>
                  <ul>
                    <li>ABN</li>
                    <li>Legal business name</li>
                    <li>Trading name (if different from legal name)</li>
                    <li>Business address</li>
                    <li>Business phone number</li>
                    <li>Business website URL <span className="sid-note">(required by ACMA — a social media profile is acceptable if you have no website)</span></li>
                  </ul>
                </div>
                <div className="sid-info-card">
                  <div className="sid-info-label">Contact person</div>
                  <ul>
                    <li>Full name</li>
                    <li>Email address <span className="sid-note">(must match the ABR contact email)</span></li>
                    <li>Phone number</li>
                  </ul>
                  <div className="sid-info-label" style={{marginTop: '20px'}}>Sender ID details</div>
                  <ul>
                    <li>Your sender ID (e.g. your business name as it appears in SMS)</li>
                    <li>Use case <span className="sid-note">(e.g. Registered Business Name, Registered Domain Name)</span></li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <div className="sid-divider" />

          {/* Section 6 — Timeline */}
          <section className="sid-section" id="timeline">
            <div className="sid-section-number">6</div>
            <div className="sid-section-body">
              <h2>Timeline</h2>
              <p>
                The deadline is <strong>1 July 2026</strong>. We strongly recommend submitting your details to DataFlows as soon as possible.
              </p>
              <div className="sid-timeline">
                <div className="sid-timeline-item sid-timeline-done">
                  <div className="sid-timeline-dot" />
                  <div className="sid-timeline-content">
                    <strong>Now</strong>
                    <span>Check your ABR details are current. Set up myID if you haven't already.</span>
                  </div>
                </div>
                <div className="sid-timeline-item">
                  <div className="sid-timeline-dot" />
                  <div className="sid-timeline-content">
                    <strong>Provide details to DataFlows</strong>
                    <span>Submit your business information via your DataFlows account or by contacting support.</span>
                  </div>
                </div>
                <div className="sid-timeline-item">
                  <div className="sid-timeline-dot" />
                  <div className="sid-timeline-content">
                    <strong>DataFlows submits to ACMA</strong>
                    <span>We lodge the registration application on your behalf.</span>
                  </div>
                </div>
                <div className="sid-timeline-item">
                  <div className="sid-timeline-dot" />
                  <div className="sid-timeline-content">
                    <strong>You receive ACMA confirmation email</strong>
                    <span>Click the link, log in with myID, confirm your registration in ACMA Assist.</span>
                  </div>
                </div>
                <div className="sid-timeline-item sid-timeline-deadline">
                  <div className="sid-timeline-dot" />
                  <div className="sid-timeline-content">
                    <strong>1 July 2026 — Deadline</strong>
                    <span>Unregistered sender IDs will be labelled "Unverified" from this date.</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <div className="sid-divider" />

          {/* Section 7 — myID */}
          <section className="sid-section" id="myid">
            <div className="sid-section-number">7</div>
            <div className="sid-section-body">
              <h2>Haven't set up myID yet?</h2>
              <p>
                myID is the Australian Government's digital identity app. Your authorised representative needs it to log in and confirm the ACMA registration — without it, they cannot complete the process.
              </p>
              <p>
                <strong>Set it up now so you're ready when the ACMA confirmation email arrives.</strong> Don't wait until you receive the email — myID verification can take a little time if you need to provide identity documents.
              </p>
              <a
                href="https://www.myid.gov.au"
                target="_blank"
                rel="noopener noreferrer"
                className="sid-external-link"
              >
                Set up myID at myid.gov.au
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M10 6H6C4.89543 6 4 6.89543 4 8V18C4 19.1046 4.89543 20 6 20H16C17.1046 20 18 19.1046 18 18V14M14 4H20M20 4V10M20 4L10 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </a>
            </div>
          </section>

          <div className="sid-divider" />

          {/* Section 8 — FAQ */}
          <section className="sid-section" id="faq">
            <div className="sid-section-number" style={{visibility: 'hidden'}}>8</div>
            <div className="sid-section-body">
              <h2>Common questions</h2>
              <div className="sid-faqs">
                <div className="sid-faq">
                  <h3>What happens if my sender ID is not registered by 1 July 2026?</h3>
                  <p>Your SMS messages will be automatically labelled "Unverified" by Telstra, Optus, and Vodafone. Customers will see this warning, which can reduce trust and may lower your open and response rates.</p>
                </div>
                <div className="sid-faq">
                  <h3>Can I register multiple sender IDs?</h3>
                  <p>Yes. If your business uses more than one sender ID, each one needs to be registered separately. Contact DataFlows support and we'll handle each submission.</p>
                </div>
                <div className="sid-faq">
                  <h3>What if my authorised representative has changed?</h3>
                  <p>Update your ABR details first at <a href="https://abr.business.gov.au" target="_blank" rel="noopener noreferrer">abr.business.gov.au</a>, then let us know when you provide your registration details. The person confirming with ACMA must match the ABR at the time of submission.</p>
                </div>
                <div className="sid-faq">
                  <h3>Does this affect all Australian businesses sending SMS?</h3>
                  <p>Yes — any business using an alphanumeric sender ID (your business name instead of a number) in Australia must register it. This applies regardless of which SMS provider you use. As your provider, DataFlows manages the ACMA submission step for you.</p>
                </div>
              </div>
            </div>
          </section>

        </div>
      </div>

      {/* CTA Section */}
      <section className="sid-cta">
        <div className="sid-inner">
          <h2>Ready to get your sender ID registered?</h2>
          <p>Contact our support team and we'll walk you through the process. Don't leave it until June — allow time for ABR updates and ACMA processing.</p>
          <div className="sid-cta-buttons">
            <Link to="/contact-us" className="sid-btn-primary">Contact DataFlows support</Link>
            <a href="https://sms.dataflows.com.au" target="_blank" rel="noopener noreferrer" className="sid-btn-outline">Log in to your account</a>
          </div>
          <p className="sid-cta-deadline">Deadline: 1 July 2026</p>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default SenderIdRegistration
